import { useI18n } from '../../../shared/i18n';
import { TERMINAL_METRICS } from '../landingMetrics';
import { BinaryRain } from '../../../shared/ui';

/**
 * The terminal session shown inside the card. Illustrative, not a status
 * report: a fixed example of what a deploy through Bitwize looks like — the
 * same kind of fixed flavour text as a code sample, not a live or historical
 * claim about any specific run. It stays `dir="ltr"` and untranslated, the
 * treatment every technical token on the site gets (route markers, version
 * strings): a shell session is an artifact, not prose in the visitor's
 * language. `aria-hidden` for the same reason — it says nothing a
 * screen-reader user needs, and read aloud it is noise.
 */
const LOG_LINES: ReadonlyArray<{ text: string; tone?: 'ok' }> = [
  { text: '$ deploy --env=production' },
  { text: '✓ build passed', tone: 'ok' },
  { text: '✓ tests 428/428', tone: 'ok' },
  { text: '✓ zero-downtime rollout', tone: 'ok' },
];

/**
 * The hero's boxed dark card (Bitwize logo approval mock, 2026-08).
 *
 * Two different kinds of content, deliberately treated differently: the
 * terminal above is decoration and is hidden from assistive technology,
 * while the metric strip along the bottom is a real published claim
 * (`landingMetrics.ts`) and is exposed as ordinary content, mirrored in RTL
 * and translated like any other copy.
 */
export function HeroTerminalCard() {
  const { t } = useI18n();

  return (
    /*
      Three nested elements so that no element animates more than one
      property: this one drifts (`transform`), the card inside it does the
      one-shot entrance, and the glow behind it pulses (`opacity`). See
      `.hs-terminal-float` in global.css for why that split is what makes
      the loop smooth.
    */
    <div className="hs-terminal-float relative">
      {/*
        The pulsing halo, *outside* the card and behind it — the card clips
        its own overflow, so a glow that has to bleed past the corners
        cannot live inside it. Purely decorative.
      */}
      <div
        aria-hidden="true"
        className="hs-terminal-glow pointer-events-none absolute inset-6 -z-10 rounded-2xl bg-accent/25 blur-[40px]"
      />

      <div className="hs-terminal-card relative flex h-[420px] flex-col overflow-hidden rounded-2xl border border-border-translucent bg-surface">
        {/*
          The mock's light source: a blurred accent bloom bleeding in from the
          card's top-right corner, behind everything else in it. Decorative and
          non-interactive — it exists so the card reads as lit rather than as a
          flat panel pasted onto the page.
        */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -end-15 -top-15 size-70 rounded-full bg-[radial-gradient(circle,var(--hs-color-accent),transparent_70%)] opacity-30 blur-[10px]"
        />

        <div
          dir="ltr"
          aria-hidden="true"
          className="relative z-10 flex flex-none items-center gap-2 border-b border-border-translucent px-4.5 py-3.5"
        >
          {/*
          The window buttons and the ✓ below are the one place tier 1 is the
          right reference. They are not status in this system's sense — the
          card reports nothing — they are the colours a terminal window is
          drawn in, and mapping them to `--hs-color-danger` / `-warning` /
          `-success` would make a repaint of the alert palette silently
          restyle a picture of a shell.
        */}
          <span className="size-2.5 rounded-full bg-[var(--hs-red-500)]" />
          <span className="size-2.5 rounded-full bg-[var(--hs-amber-500)]" />
          <span className="size-2.5 rounded-full bg-[var(--hs-green-500)]" />
          <span className="hs-meta ms-2 truncate text-text-subtle">bitwize@prod — deploy</span>
        </div>

        {/*
        `dir="ltr"` on the body, not just on the log lines: the watermark is
        placed with a logical `end-*` offset, and in Arabic that would
        otherwise resolve to the left and sit on top of the session text.
        A shell window does not mirror — the whole body is one LTR artifact.
        The metric strip below is outside this and mirrors normally.
      */}
        <div dir="ltr" className="relative flex-1 overflow-hidden">
          {/* Falling binary masked to the mark's own silhouette. The solid
            cut, from `make logo` — a mask reads alpha, so the lattice is what
            shapes the fall and the file's own ink never reaches the screen. */}
          <BinaryRain
            columns={12}
            rows={40}
            tone="mark"
            mask="url(/brand/mark-solid.svg)"
            className="absolute end-[-40px] top-1/2 size-[300px] -translate-y-1/2 opacity-55"
          />

          <div
            aria-hidden="true"
            className="relative z-10 flex flex-col gap-2 p-6 font-mono text-[13px] leading-loose text-text-muted"
          >
            {LOG_LINES.map((line, i) => (
              <div
                key={line.text}
                className="hs-terminal-line"
                style={{ animationDelay: `${0.2 + i * 0.5}s` }}
              >
                {line.tone === 'ok' ? (
                  <span className="text-[var(--hs-green-500)]">{line.text}</span>
                ) : (
                  <>
                    <span className="text-accent">$</span> {line.text.slice(2)}
                  </>
                )}
              </div>
            ))}
            <div
              className="hs-terminal-line"
              style={{ animationDelay: `${0.2 + LOG_LINES.length * 0.5}s` }}
            >
              <span className="text-accent">$</span>{' '}
              <span className="hs-terminal-caret border-e-2 border-accent">&nbsp;</span>
            </div>
          </div>
        </div>

        {TERMINAL_METRICS.length > 0 ? (
          <dl className="relative z-10 flex flex-none justify-between gap-4 border-t border-border-translucent px-6 py-4">
            {TERMINAL_METRICS.map((metric) => (
              <div key={metric.labelKey}>
                <dd dir="ltr" className="font-heading text-lg font-semibold text-text">
                  {metric.value}
                </dd>
                <dt className="hs-meta text-[10.5px] text-text-subtle">{t(metric.labelKey)}</dt>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </div>
  );
}
